import classNames from "classnames";
import Image, { type ImageProps } from "~/components/common/Image";
import { arrayIsNonEmpty } from "~/util/array";
import {
  type ListItemProps,
  type ListMarkerProps,
  type ListVariant,
} from "./types";
import Accordion from "~/components/common/Accordion";
import Button from "~/components/common/Button";
import ButtonContainer from "~/components/common/ButtonContainer";
import Heading from "~/components/common/Heading";
import RichText from "~/components/common/RichText";

const ImageMarker = ({ image }: { image: ImageProps }) => (
  <div className="flex items-start">
    <Image {...image} ariaHidden={true} />
  </div>
);

const StyledMarker = ({
  index,
  variant,
}: {
  index?: number;
  variant: ListVariant;
}) => (
  <div
    className={classNames("shrink-0 flex justify-center items-center", {
      "w-[16px] h-[2px] border border-black mt-[16px] text-black text-black! forced-color-adjust-auto":
        variant === "unordered",
      "h-[40px] w-full border-2 border-kern-layout-text-default bg-kern-layout-text-default rounded-full text-kern-layout-text-inverse! font-semibold forced-color-adjust-auto":
        variant === "numbered",
      "h-[40px] w-full border-2 border-kern-layout-text-default bg-kern-layout-text-default text-kern-layout-text-inverse! font-semibold rounded-full forced-color-adjust-auto":
        variant === "stepByStep",
    })}
  >
    {variant === "unordered" ? null : index}
  </div>
);

const ListMarker = ({ index, variant, image }: ListMarkerProps) => {
  return image ? (
    <ImageMarker image={image} />
  ) : (
    <StyledMarker index={index} variant={variant} />
  );
};

const ListItem = ({
  identifier,
  headline,
  content,
  buttons,
  index,
  accordion,
  variant,
  image,
}: ListItemProps & { variant: ListVariant }) => {
  return (
    <div
      id={identifier}
      className="flex lg:flex-row flex-col gap-kern-space-default pb-48"
    >
      <div className="text-center shrink-0 flex flex-col items-center w-[40px]">
        <ListMarker index={index} variant={variant} image={image} />
        {variant === "stepByStep" && (
          <div className="w-2 h-full group-last:hidden"></div>
        )}
      </div>
      <div className="flex-1 min-w-0 gap-kern-space-x-large">
        {headline && (
          <Heading
            {...headline}
            className="text-kern-static-large! mt-3!"
            managedByParent
          />
        )}
        {content && <RichText className="pt-8! leading-24!" html={content} />}
        {arrayIsNonEmpty(buttons) && (
          <ButtonContainer className="mt-16">
            {buttons.map((button) => (
              <Button key={button.text ?? button.href} {...button} />
            ))}
          </ButtonContainer>
        )}

        {accordion && (
          <div className="pt-kern-space-x-large">
            <Accordion {...accordion} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ListItem;
